import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ProctoringWidget from './widget/ProctoringWidget';
import SecureExamProvider from '../../../src/security/SecureExamProvider';
import { useSecureExam } from '../../../src/security/useSecureExam';
import { isClosedBookEnabled, mergePolicy } from '../../../src/security/securityPolicy';
import { apiFetch } from './services/api';
import { resolveApiBaseUrl } from './constants';
import { isClosedBookEmbedFlag } from './closedBookFlag';
import { isClosedBookLaunchPeriod, parseExamTimestamp } from './examTiming';

export { isClosedBookEmbedFlag };

function useClosedBookExamLive(widgetProps) {
  const [now, setNow] = useState(() => Date.now());
  const startTs = parseExamTimestamp(widgetProps.examStartAt);
  const examLive = isClosedBookLaunchPeriod({
    examIsActive: widgetProps.examIsActive,
    start: widgetProps.start,
    examStartAt: widgetProps.examStartAt,
    endExam: widgetProps.endExam,
    systemCheckMinutesBeforeExam: widgetProps.systemCheckMinutesBeforeExam,
    now,
  });

  useEffect(() => {
    if (widgetProps.endExam) return undefined;
    if (widgetProps.examIsActive === true || widgetProps.start === true) return undefined;
    if (startTs == null) return undefined;
    const delay = Math.max(0, startTs - Date.now());
    const t = setTimeout(() => setNow(Date.now()), Math.min(delay + 50, 60000));
    const poll = setInterval(() => setNow(Date.now()), 15000);
    return () => {
      clearTimeout(t);
      clearInterval(poll);
    };
  }, [startTs, widgetProps.endExam, widgetProps.examIsActive, widgetProps.start]);

  return examLive;
}

function ClosedBookWidgetBridge({ widgetProps, forwardedRef }) {
  const { finalizeAndSubmit, markEnvironmentCheckComplete, screenShareStream } = useSecureExam();
  const prevEnd = useRef(!!widgetProps.endExam);
  const parentOnReadyRef = useRef(widgetProps.onReadyForMonitoring);
  const parentOnSystemCheckRef = useRef(widgetProps.onSystemCheckComplete);
  parentOnReadyRef.current = widgetProps.onReadyForMonitoring;
  parentOnSystemCheckRef.current = widgetProps.onSystemCheckComplete;

  useEffect(() => {
    if (widgetProps.requireSystemCheck === false) {
      markEnvironmentCheckComplete();
    }
  }, [markEnvironmentCheckComplete, widgetProps.requireSystemCheck]);

  useEffect(() => {
    if (widgetProps.endExam && !prevEnd.current) {
      finalizeAndSubmit();
    }
    prevEnd.current = !!widgetProps.endExam;
  }, [finalizeAndSubmit, widgetProps.endExam]);

  const handleReadyForMonitoring = useCallback((ready) => {
    if (ready) markEnvironmentCheckComplete();
    if (typeof parentOnReadyRef.current === 'function') {
      parentOnReadyRef.current(ready);
    }
  }, [markEnvironmentCheckComplete]);

  const handleSystemCheckComplete = useCallback((data) => {
    markEnvironmentCheckComplete();
    if (typeof parentOnSystemCheckRef.current === 'function') {
      parentOnSystemCheckRef.current(data);
    }
  }, [markEnvironmentCheckComplete]);

  return (
    <ProctoringWidget
      {...widgetProps}
      ref={forwardedRef}
      start={widgetProps.start}
      examIsActive={widgetProps.examIsActive}
      screenShareStream={screenShareStream}
      onReadyForMonitoring={handleReadyForMonitoring}
      onSystemCheckComplete={handleSystemCheckComplete}
    />
  );
}

function useClosedBookFromExam(examIds, apiBaseUrl, embedFlag, embedPolicy) {
  const embedOn = isClosedBookEmbedFlag(embedFlag);
  const [remote, setRemote] = useState(null);
  const idKey = Array.from(new Set(
    (examIds || []).filter((id) => id != null && String(id).trim() !== '').map((id) => String(id).trim())
  )).join('|');

  useEffect(() => {
    const ids = idKey ? idKey.split('|') : [];
    if (embedOn || ids.length === 0) {
      setRemote(null);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const base = resolveApiBaseUrl(apiBaseUrl);
        let chosen = { enabled: false, policy: mergePolicy({}) };
        for (const examId of ids) {
          const res = await apiFetch(base, `/api/exams/${encodeURIComponent(examId)}/security-policy`);
          const json = await res.json().catch(() => ({}));
          if (cancelled) return;
          const enabled = json.enabled === true || isClosedBookEnabled({
            examEnabled: json.examEnabled,
            featureFlagEnabled: json.featureFlagEnabled,
            hasTenant: json.hasTenant
          });
          if (enabled) {
            chosen = { enabled: true, policy: mergePolicy(json.policy) };
            break;
          }
        }
        setRemote((prev) => {
          if (prev && prev.enabled === chosen.enabled && JSON.stringify(prev.policy) === JSON.stringify(chosen.policy)) {
            return prev;
          }
          return chosen;
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ClosedBook] security-policy fetch failed', err && (err.message || String(err)));
        if (!cancelled) {
          setRemote((prev) => (prev && prev.enabled === false ? prev : { enabled: false, policy: mergePolicy({}) }));
        }
      }
    };
    load();
    const timer = setInterval(load, 10000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [apiBaseUrl, embedOn, idKey]);

  return useMemo(() => {
    if (embedOn) {
      return { enabled: true, policy: mergePolicy(embedPolicy) };
    }
    return {
      enabled: !!(remote && remote.enabled),
      policy: mergePolicy({ ...(remote && remote.policy), ...(embedPolicy || {}) })
    };
  }, [embedOn, embedPolicy, remote]);
}

const ProctoringWidgetWithClosedBook = forwardRef(function ProctoringWidgetWithClosedBook(props, ref) {
  const { closedBookSecurity, closedBookPolicy, ...widgetProps } = props;
  const examRoomId = widgetProps.adminExamId || widgetProps.libraryId || widgetProps.examId;
  const apiBaseUrl = widgetProps.apiBaseUrl;
  const resolvedBase = resolveApiBaseUrl(apiBaseUrl);
  const request = useCallback((path, options) => apiFetch(resolvedBase, path, options), [resolvedBase]);
  const examLive = useClosedBookExamLive(widgetProps);
  const closedBook = useClosedBookFromExam(
    [widgetProps.examId, widgetProps.adminExamId, widgetProps.libraryId, examRoomId],
    resolvedBase,
    closedBookSecurity,
    closedBookPolicy
  );

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[ClosedBook] widget layer', {
      enabled: closedBook.enabled,
      examLive,
      examId: examRoomId,
      examIsActive: widgetProps.examIsActive,
      start: widgetProps.start,
      examStartAt: widgetProps.examStartAt,
      endExam: widgetProps.endExam,
      embedFlag: closedBookSecurity,
    });
  }, [
    closedBook.enabled,
    closedBookSecurity,
    examLive,
    examRoomId,
    widgetProps.endExam,
    widgetProps.examIsActive,
    widgetProps.examStartAt,
    widgetProps.start,
  ]);

  return (
    <SecureExamProvider
      enabled={closedBook.enabled}
      examLive={examLive}
      examId={examRoomId}
      candidateId={widgetProps.userId}
      policy={closedBook.policy}
      request={request}
      requireEnvironmentCheck={false}
    >
      <ClosedBookWidgetBridge widgetProps={widgetProps} forwardedRef={ref} />
    </SecureExamProvider>
  );
});

ProctoringWidgetWithClosedBook.propTypes = {
  ...ProctoringWidget.propTypes,
  closedBookSecurity: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
  closedBookPolicy: PropTypes.object
};

ProctoringWidgetWithClosedBook.defaultProps = {
  ...(ProctoringWidget.defaultProps || {}),
  closedBookSecurity: false
};

export default ProctoringWidgetWithClosedBook;
